import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Icon } from '@fashionnovateam/nova-ui-toolkit';
import debounce from 'lodash/debounce';
import Page from '../Page';
import Header from '../Header';
import { User } from '../../Lib/Api/vendor';
import { useCounter } from './hooks/queries';
import CounterDatagrid from './CounterDatagrid';
import { CounterProvider } from './contexts/counter';
import {
  SampleRequestFilters,
  readFiltersFromUrl,
  useUrlFilters,
} from './filters/sample-request-filters';
import type { SampleRequestFilterValues } from './filters/sample-request-filters';

type RequestedItemsProps = {
  user: User;
  location: { search: string };
};

function RequestedItemsV2(props: RequestedItemsProps) {
  const urlParams = new URLSearchParams(props.location.search);
  const counterIdParam = urlParams.get('counter_id');
  const history = useHistory();
  const vendorId = props?.user?.vendor?.internal_id;
  const userId = props?.user?.id;
  const contacts = props?.user?.vendor?.contacts || [];
  if (!vendorId) throw new Error('Vendor internal id not found');

  const initialUrlState = useRef(readFiltersFromUrl(props.location.search));
  const { syncToUrl } = useUrlFilters();

  const [searchValue, setSearchValue] = useState(
    initialUrlState.current.search,
  );
  const [paginationParams, setPaginationParams] = useState({
    page: 1,
    pageSize: 50,
    vendorContacts: initialUrlState.current.vendorContacts,
    priorities: initialUrlState.current.priorities,
    quoteStatus: initialUrlState.current.quoteStatus,
    prevSampleStatus: initialUrlState.current.prevSampleStatus,
    counterSampleStatus: initialUrlState.current.counterSampleStatus,
    sortedBy: 'last_message.updated_at' as string,
    sortOrder: 'DESC' as string | null | undefined,
    counterIds: counterIdParam ? [counterIdParam] : ([] as string[]),
    search: initialUrlState.current.search,
    filters: initialUrlState.current.filters,
  });

  const initialFilterValue = useMemo<SampleRequestFilterValues>(
    () => ({
      quoteStatus: initialUrlState.current.quoteStatus,
      prevSampleStatus: initialUrlState.current.prevSampleStatus,
      counterSampleStatus: initialUrlState.current.counterSampleStatus,
      priorities: initialUrlState.current.priorities,
      vendorContacts: initialUrlState.current.vendorContacts,
      filters: initialUrlState.current.filters,
    }),
    [],
  );

  useEffect(() => {
    syncToUrl({
      search: paginationParams.search,
      quoteStatus: paginationParams.quoteStatus,
      prevSampleStatus: paginationParams.prevSampleStatus,
      counterSampleStatus: paginationParams.counterSampleStatus,
      priorities: paginationParams.priorities,
      vendorContacts: paginationParams.vendorContacts,
      filters: paginationParams.filters,
    });
  }, [
    paginationParams.search,
    paginationParams.quoteStatus,
    paginationParams.prevSampleStatus,
    paginationParams.counterSampleStatus,
    paginationParams.priorities,
    paginationParams.vendorContacts,
    paginationParams.filters,
    syncToUrl,
  ]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setPaginationParams((p) => ({ ...p, page: 1, search: value }));
      }, 500),
    [],
  );
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  const handleFilterChange = useCallback(
    (values: SampleRequestFilterValues) => {
      setPaginationParams((p) => ({ ...p, page: 1, ...values }));
    },
    [],
  );

  const counterQuery = useCounter(userId, {
    page: paginationParams.page,
    pageSize: paginationParams.pageSize,
    vendorContacts: paginationParams.vendorContacts,
    priorities: paginationParams.priorities,
    quoteStatus: paginationParams.quoteStatus,
    prevSampleStatus: paginationParams.prevSampleStatus,
    counterSampleStatus: paginationParams.counterSampleStatus,
    sortedBy: paginationParams.sortedBy,
    sortOrder: paginationParams.sortOrder,
    counterIds: paginationParams.counterIds,
    search: paginationParams.search,
    filters: paginationParams.filters,
  });

  return (
    <CounterProvider contacts={contacts}>
      <Page.Container>
        <Header.Major icon="inbox/inbox-default" title="Sample Requests" />
        <Header.Minor
          search={{
            value: searchValue,
            onChange: handleSearchChange,
            placeholder: 'Search by style or display name..',
            position: 'left',
            className: 'min-w-72',
          }}
        >
          {counterIdParam && (
            <Button
              size="sm"
              color="secondary"
              onClick={() => {
                history.push('sample-requests');
                setPaginationParams((p) => ({ ...p, counterIds: [] }));
              }}
            >
              <Icon name="filter/filter-horizontal" />
              Filtered by ID
              <Icon name="multiple-cross/multiple-cross-cancel-default" />
            </Button>
          )}
        </Header.Minor>
        <SampleRequestFilters
          contacts={contacts}
          initialValue={initialFilterValue}
          onChange={handleFilterChange}
        />
        <Page.Content>
          <CounterDatagrid
            counterIdParam={counterIdParam}
            vendorId={vendorId}
            userId={userId}
            key={`${paginationParams.page}-${paginationParams.pageSize}`}
            isLoading={counterQuery.isLoading}
            data={counterQuery.data?.data ?? []}
            rowCount={counterQuery.data?.total || 0}
            pageSizeOptions={[10, 25, 50, 100]}
            paginationModel={{
              pageSize: paginationParams.pageSize,
              page: paginationParams?.page ? paginationParams.page - 1 : 0,
            }}
            onPaginationModelChange={(model) => {
              setPaginationParams((p) => ({
                ...p,
                pageSize: model.pageSize,
                page: model.page + 1,
              }));
            }}
            sortModel={
              paginationParams?.sortOrder
                ? [
                    {
                      field: paginationParams.sortedBy,
                      sort:
                        paginationParams.sortOrder === 'asc' ? 'asc' : 'desc',
                    },
                  ]
                : []
            }
            onSortModelChange={(model) => {
              const sortItem = model[0];
              setPaginationParams((p) => ({
                ...p,
                sortedBy: sortItem?.field,
                sortOrder: sortItem?.sort,
              }));
            }}
          />
        </Page.Content>
      </Page.Container>
    </CounterProvider>
  );
}

export default RequestedItemsV2;
